import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import CreatableSelect from "react-select/creatable";
import { API_URL } from "../../../config";

function AddProject() {
  const { clientId: routeClientId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const clientData = location.state?.client;

  const [departments, setDepartments] = useState([]);
  const [services, setServices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [categories] = useState([
    { value: "Laravel", label: "Laravel" },
    { value: "Vuejs", label: "Vuejs" },
    { value: "React", label: "React" },
    { value: "Zend", label: "Zend" },
    { value: "CakePhp", label: "CakePhp" },
  ]);

  const [formData, setFormData] = useState({
    projectName: "",
    department: "",
    service: "",
    startDate: "",
    endDate: "",
    projectCategory: [],
    addMember: [],
    clientId: clientData?._id || routeClientId || "",
    displayClientId: clientData?.leadId || "",
    clientName: clientData?.leadName || "",
    notes: "",
    addFile: null,
    projectDescription: "",
    price: "",
  });

  const selectedService = useMemo(
    () => services.find((s) => s._id === formData.service),
    [services, formData.service]
  );

  useEffect(() => {
    const resolvedClientId = clientData?._id || routeClientId || "";
    const resolvedLeadId = clientData?.leadId || "";
    const resolvedName = clientData?.leadName || "";

    setFormData((prev) => ({
      ...prev,
      clientId: resolvedClientId,
      displayClientId: resolvedLeadId,
      clientName: resolvedName,
    }));
  }, [clientData, routeClientId]);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/getDepartment`)
      .then((res) => setDepartments(Array.isArray(res.data) ? res.data : []))
      .catch(() => setDepartments([]));
  }, []);

  useEffect(() => {
    if (!formData.department) {
      setServices([]);
      setEmployees([]);
      setFormData((prev) => ({ ...prev, service: "", price: "" }));
      return;
    }

    axios
      .get(`${API_URL}/api/getServicebyDepartment/${formData.department}`)
      .then((res) => setServices(Array.isArray(res.data) ? res.data : []))
      .catch(() => setServices([]));

    axios
      .get(`${API_URL}/api/getEmployeeByDepartment/${formData.department}`)
      .then((res) => setEmployees(Array.isArray(res.data) ? res.data : []))
      .catch(() => setEmployees([]));
  }, [formData.department]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (e) => {
    const serviceId = e.target.value;
    const serviceObj = services.find((s) => s._id === serviceId);

    setFormData((prev) => ({
      ...prev,
      service: serviceId,
      price: serviceObj?.servicePrice ?? "",
    }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({ ...prev, addFile: e.target.files?.[0] || null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.clientId) {
      alert("Client is missing. Please open Add Project from Client section.");
      return;
    }

    if (formData.startDate && formData.endDate && new Date(formData.startDate) > new Date(formData.endDate)) {
      alert("End date cannot be before start date.");
      return;
    }

    try {
      setSubmitting(true);

      const payload = new FormData();
      payload.append("projectName", formData.projectName);
      payload.append("department", formData.department);
      payload.append("service", formData.service);
      payload.append("startDate", formData.startDate);
      payload.append("endDate", formData.endDate);
      payload.append("projectDescription", formData.projectDescription);
      payload.append("notes", formData.notes || "");
      payload.append("clientId", formData.clientId);
      payload.append("leadId", formData.displayClientId || "");
      payload.append("displayClientId", formData.displayClientId || "");

      if (formData.price !== "") {
        payload.append("price", formData.price);
      }

      formData.projectCategory.forEach((cat) => {
        payload.append("projectCategory[]", cat);
      });
      payload.append("addMember", JSON.stringify(formData.addMember || []));

      if (formData.addFile) {
        payload.append("addFile", formData.addFile);
      }

      await axios.post(`${API_URL}/api/addProject`, payload, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
        },
      });

      alert("Project added successfully!");
      navigate("/admin/getProjectList");
    } catch (err) {
      const message = err?.response?.data?.error || err?.response?.data?.message || "Error adding project.";
      alert(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container mt-4">
      <button className="btn btn-outline-secondary mb-3" onClick={() => navigate(-1)}>
        Back
      </button>

      <div className="card shadow-lg">
        <div
          className="card-header text-center"
          style={{ background: "linear-gradient(105deg, #c9d9ea, #c0d2e4 60%, #b5c9de)", color: "#0f172a" }}
        >
          <h4 className="mb-0">Add New Project</h4>
        </div>

        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row mb-3">
              <div className="col-md-6">
                <label className="fw-bold">Client ID</label>
                <input className="form-control" value={formData.displayClientId || formData.clientId} disabled />
              </div>
              <div className="col-md-6">
                <label className="fw-bold">Client Name</label>
                <input className="form-control" value={formData.clientName || "-"} disabled />
              </div>
            </div>

            <div className="mb-3">
              <label className="fw-bold">Project Name *</label>
              <input type="text" name="projectName" className="form-control" value={formData.projectName} onChange={handleChange} required />
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <label className="fw-bold">Department *</label>
                <select name="department" className="form-select" value={formData.department} onChange={handleChange} required>
                  <option value="">Select</option>
                  {departments.map((d) => (
                    <option key={d._id} value={d._id}>
                      {d.deptName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6">
                <label className="fw-bold">Service *</label>
                <select name="service" className="form-select" value={formData.service} onChange={handleServiceChange} required>
                  <option value="">Select</option>
                  {services.map((s) => (
                    <option key={s._id} value={s._id}>
                      {s.serviceName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <label className="fw-bold">Start Date *</label>
                <input type="date" name="startDate" className="form-control" value={formData.startDate} onChange={handleChange} required />
              </div>
              <div className="col-md-6">
                <label className="fw-bold">End Date *</label>
                <input type="date" name="endDate" className="form-control" value={formData.endDate} onChange={handleChange} required />
              </div>
            </div>

            <div className="mb-3">
              <label className="fw-bold">Categories / Technology</label>
              <CreatableSelect
                isMulti
                options={categories}
                value={formData.projectCategory.map((cat) => ({ value: cat, label: cat }))}
                onChange={(newValue) =>
                  setFormData((prev) => ({
                    ...prev,
                    projectCategory: newValue ? newValue.map((item) => item.value) : [],
                  }))
                }
                placeholder="Select or type categories..."
              />
            </div>

            <div className="mb-3">
              <label className="fw-bold">Project Description *</label>
              <textarea name="projectDescription" className="form-control" rows="4" value={formData.projectDescription} onChange={handleChange} required />
            </div>

            <div className="mb-3">
              <label className="fw-bold">Assign Members</label>
              <select
                className="form-select"
                multiple
                value={formData.addMember}
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions).map((opt) => opt.value);
                  setFormData((prev) => ({ ...prev, addMember: selected }));
                }}
              >
                {employees.map((emp) => (
                  <option key={emp._id} value={emp._id}>
                    {emp.ename || emp.name || emp.employeeId || emp._id}
                  </option>
                ))}
              </select>
              <small className="text-muted">Hold Ctrl (Windows) / Cmd (Mac) to select multiple members.</small>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <label className="fw-bold">Price</label>
                <input
                  type="number"
                  min="0"
                  name="price"
                  className="form-control"
                  value={formData.price}
                  onChange={handleChange}
                  placeholder={selectedService?.servicePrice ? `Suggested: ${selectedService.servicePrice}` : ""}
                />
              </div>
              <div className="col-md-6">
                <label className="fw-bold">Upload File</label>
                <input type="file" className="form-control" onChange={handleFileChange} />
              </div>
            </div>

            <div className="mb-3">
              <label className="fw-bold">Notes</label>
              <textarea name="notes" className="form-control" rows="2" value={formData.notes} onChange={handleChange} />
            </div>

            <button
              type="submit"
              className="btn w-100"
              style={{ background: "linear-gradient(105deg, #c9d9ea, #c0d2e4 60%, #b5c9de)", color: "#0b4f84" }}
              disabled={submitting}
            >
              {submitting ? "Saving..." : "Save Project"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AddProject;
